import { useState } from "react";

import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import type { PlanningConfig } from "@/api/planningConfig";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { usePlanLens } from "@/global/PlanLensContext";

import DemandChart from "./demand-chart/DemandChart";
import PlanningConfigPanel from "./PlanningConfigPanel";
import StockChart from "./stock-chart/StockChart";

interface LocationPanelProps {
  itemId: number;
  locationId: number;
  config: PlanningConfig | null;
}

/**
 * The Location layer: charts always render for the node. The right rail
 * holds forecast settings + replenishment policy; a config row exists only once
 * the node has demand history, and its presence enables the forecast curve.
 */
export default function LocationPanel({
  itemId,
  locationId,
  config,
}: LocationPanelProps) {
  const { t } = useTranslation();
  const { run } = usePlanLens();
  // Under the lens the run's plan date is the start; otherwise this day, or today.
  const [asOf, setAsOf] = useState("");

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="grid grid-cols-[minmax(0,1fr)_280px] gap-6">
        <Card className="min-w-0">
          <CardContent>
            <DemandChart
              itemId={itemId}
              locationIds={[locationId]}
              forecastEnabled={config !== null}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <PlanningConfigPanel
              itemId={itemId}
              locationId={locationId}
              config={config}
            />
          </CardContent>
        </Card>
      </div>

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>{t("items.detail.stockPrediction.title")}</CardTitle>
          {!run && (
            <CardAction className="flex items-center gap-1">
              {asOf && (
                <Button variant="ghost" size="sm" onClick={() => setAsOf("")}>
                  {t("items.detail.stockPrediction.today")}
                </Button>
              )}
              <Input
                type="date"
                aria-label={t("items.detail.stockPrediction.asOf")}
                value={asOf}
                max={dayjs().format("YYYY-MM-DD")}
                onChange={(e) => setAsOf(e.target.value)}
                className="h-8 w-auto"
              />
            </CardAction>
          )}
        </CardHeader>
        <CardContent className="min-w-0">
          <StockChart
            itemId={itemId}
            locationId={locationId}
            runId={run?.id}
            asOf={asOf || undefined}
          />
        </CardContent>
      </Card>
    </div>
  );
}
